import { getTranslation } from '@mahawi/eshop-common/dist/src/translation';
import {
  EEventContextAction,
  EEventType,
  type EFeatureFlag,
  EPropertyType,
  type IBrand,
  type ICategory,
  type IProductList,
  type IPropertyFilter,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { getHead } from '../../head';
import Breadcrumbs from '../components/product/breadcrumbs';
import FeatureFlagQuickFilter, {
  hasVisibleFeatureFlagOptions,
} from '../components/products/feature-flag-quick-filter';
import Grid from '../components/products/grid';
import LanguageMutationLinks from '../components/products/language-mutation-links';
import Pagination from '../components/products/pagination';
import PropertyFilters from '../components/products/property-filters';
import ProductsSkeleton from '../components/products/skeleton';
import StockQuickFilter, {
  hasVisibleStockOptions,
} from '../components/products/stock-quick-filter';
import { type ICategoryState } from '../reducers/category/types';
import { eventSend } from '../reducers/event/reducer';
import { type ILanguageState } from '../reducers/language/types';
import {
  clearPropertyFilters,
  load,
  loadAvailableValues,
  loadByBrand,
  loadByProperties,
  loadCategoryAvailability,
  loadCategoryProperties,
  loadFilteredTotal,
  resetAvailableValues,
  resetFilteredTotal,
} from '../reducers/products/reducer';
import {
  type EStockFilter,
  type IProductsState,
  STOCK_FILTER_DEFAULT,
} from '../reducers/products/types';
import { getCategoryUrl } from '../utils/category';
import {
  applyBrandToSearchParams,
  applyFeatureFlagsToSearchParams,
  applyFiltersToSearchParams,
  applyStockToSearchParams,
  BRAND_PARAM,
  parseFeatureFlagsFromSearchParams,
  parseFiltersFromSearchParams,
  parseStockFromSearchParams,
} from '../utils/product-filters-url';
const BRAND_FILTER_DEFAULT = '-';

function ProductsContainer({
  Category,
  Products,
  dispatch,
  Language,
}: {
  Category: ICategoryState;
  Products: IProductsState;
  dispatch: Dispatch;
  Language: ILanguageState;
}): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brandUUID, setBrandUUID] = useState<string>(
    () => searchParams.get(BRAND_PARAM) || BRAND_FILTER_DEFAULT,
  );
  const [stockFilter, setStockFilter] = useState<EStockFilter>(() =>
    parseStockFromSearchParams(searchParams),
  );
  const [featureFlagFilter, setFeatureFlagFilter] = useState<EFeatureFlag[]>(
    () => parseFeatureFlagsFromSearchParams(searchParams),
  );
  const [pageInt, setPageInt] = useState(1);
  const [propertyFilters, setPropertyFilters] = useState<IPropertyFilter[]>([]);
  const propertyFiltersRef = useRef<IPropertyFilter[]>([]);
  const hasAttemptedLoad = useRef(false);
  const urlFiltersAppliedForCategoryRef = useRef<string | undefined>(undefined);
  const categoryLoadHandledRef = useRef<string | undefined>(undefined);

  const { categoryUUID: categoryUUIDParam, slug, pageUrl } = useParams();
  const { t } = useTranslation();

  const categoryUUID: string | undefined =
    categoryUUIDParam ??
    (slug
      ? Category.categories.find((c: ICategory) =>
          c.slugs?.some(
            (s) => s.code === Language.languageType?.code && s.value === slug,
          ),
        )?.uuid
      : undefined);

  const refreshFilterPreview = useCallback(
    (
      filters: IPropertyFilter[],
      brand: { uuid: string } | undefined,
      stock: EStockFilter,
    ): void => {
      if (!categoryUUID) {
        return;
      }

      const activeStock: EStockFilter | undefined =
        stock === STOCK_FILTER_DEFAULT ? undefined : stock;

      if (filters.length === 0) {
        dispatch(resetFilteredTotal());
      } else {
        dispatch(
          loadFilteredTotal({
            category: { uuid: categoryUUID },
            filters,
            brand,
            stock: activeStock,
          }),
        );
      }

      const enumCodes = Products.categoryProperties
        .filter((p): boolean => p.type === EPropertyType.ENUM)
        .map((p): string => p.code);

      // availableValues narrows ENUM options by the property filter draft,
      // the selected brand AND the stock/availability filter — any one of
      // these alone (no property filters yet) still needs to hide values
      // that wouldn't otherwise be offered.
      if (
        enumCodes.length === 0 ||
        (filters.length === 0 && !brand && !activeStock)
      ) {
        dispatch(resetAvailableValues());
        return;
      }

      dispatch(
        loadAvailableValues({
          category: { uuid: categoryUUID },
          filters,
          propertyCodes: enumCodes,
          brand,
          stock: activeStock,
        }),
      );
    },
    [categoryUUID, dispatch, Products.categoryProperties],
  );

  // Single place that decides which of load/loadByBrand/loadByProperties to
  // dispatch based on which filters are currently active — every filter
  // change (brand, property, stock, feature flag) goes through this instead
  // of each handler re-deriving the same three-way branch.
  const dispatchProductsLoad = useCallback(
    ({
      filters,
      brand,
      stock,
      featureFlags,
      page,
    }: {
      filters: IPropertyFilter[];
      brand: { uuid: string } | undefined;
      stock: EStockFilter | undefined;
      featureFlags: EFeatureFlag[] | undefined;
      page: number;
    }): void => {
      if (!categoryUUID) {
        return;
      }

      if (filters.length > 0) {
        dispatch(
          loadByProperties({
            category: { uuid: categoryUUID },
            filters,
            page,
            brand,
            stock,
            featureFlags,
          }),
        );
      } else if (brand) {
        dispatch(
          loadByBrand({
            category: { uuid: categoryUUID },
            brand,
            page,
            stock,
            featureFlags,
          }),
        );
      } else {
        dispatch(
          load({
            category: { uuid: categoryUUID },
            page,
            stock,
            featureFlags,
          }),
        );
      }
    },
    [categoryUUID, dispatch],
  );

  useEffect((): void => {
    setPageInt(1);
  }, [categoryUUID]);

  useEffect((): void => {
    if (pageUrl) {
      const pageInt: number = parseInt(pageUrl, 10);
      setPageInt(pageInt);
    }
  }, [pageUrl, typeof window !== 'undefined' ? window.location.pathname : '']);

  useEffect((): (() => void) => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_PRODUCTS,
        action: EEventContextAction.ENTER,
        context: {
          page: { number: pageInt },
          category: { uuid: categoryUUID },
        },
      }),
    );

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_PRODUCTS,
          action: EEventContextAction.LEAVE,
          context: {
            page: { number: pageInt },
            category: { uuid: categoryUUID },
          },
        }),
      );
    };
  }, [dispatch, pageInt, categoryUUID]);

  const viewItemListKeyRef = useRef<string | null>(null);

  useEffect((): void => {
    if (Products.products.length === 0 || !Products.category.uuid) {
      return;
    }

    const viewItemListKey = `${Products.category.uuid}--${Products.page}`;

    if (viewItemListKeyRef.current === viewItemListKey) {
      return;
    }

    viewItemListKeyRef.current = viewItemListKey;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: Products.category.uuid,
        items: Products.products.map(
          (
            p: IProductList,
            index: number,
          ): { item_id: string; item_name: string; index: number } => {
            const productNameEn: ITranslation | undefined = p.names?.find(
              (n: ITranslation): boolean => n.code === 'EN',
            );
            return {
              item_id: p.uuid,
              item_name: productNameEn?.value || '---',
              index,
            };
          },
        ),
      },
    });
  }, [Products.products, Products.category.uuid, Products.page]);

  useEffect((): void => {
    if (
      Products.category.uuid !== '' &&
      categoryUUID !== Products.category.uuid
    ) {
      setBrandUUID(BRAND_FILTER_DEFAULT);
      setStockFilter(STOCK_FILTER_DEFAULT);
      setFeatureFlagFilter([]);
      setPropertyFilters([]);
      propertyFiltersRef.current = [];
      dispatch(clearPropertyFilters());
      setSearchParams(
        (prev: URLSearchParams): URLSearchParams =>
          applyFeatureFlagsToSearchParams(
            applyStockToSearchParams(
              applyBrandToSearchParams(
                applyFiltersToSearchParams(prev, []),
                undefined,
              ),
              undefined,
            ),
            [],
          ),
        { replace: true },
      );
    }
  }, [categoryUUID, Products.category.uuid, dispatch, setSearchParams]);

  useEffect((): void => {
    if (
      !categoryUUID ||
      Products.categoryProperties.length === 0 ||
      urlFiltersAppliedForCategoryRef.current === categoryUUID
    ) {
      return;
    }

    urlFiltersAppliedForCategoryRef.current = categoryUUID;

    const parsedFilters = parseFiltersFromSearchParams(
      searchParams,
      Products.categoryProperties,
    );

    const brand: { uuid: string } | undefined =
      brandUUID === BRAND_FILTER_DEFAULT ? undefined : { uuid: brandUUID };

    // Prime filteredTotal/availableValues even when there are no parsed
    // property filters — a brand-only deep link still needs ENUM options
    // narrowed to what that brand actually offers.
    refreshFilterPreview(parsedFilters, brand, stockFilter);

    if (parsedFilters.length === 0) {
      return;
    }

    setPropertyFilters(parsedFilters);
    propertyFiltersRef.current = parsedFilters;
    setPageInt(1);

    dispatch(
      loadByProperties({
        category: { uuid: categoryUUID },
        filters: parsedFilters,
        page: 1,
        brand,
        stock: stockFilter === STOCK_FILTER_DEFAULT ? undefined : stockFilter,
        featureFlags:
          featureFlagFilter.length > 0 ? featureFlagFilter : undefined,
      }),
    );
  }, [
    categoryUUID,
    Products.categoryProperties,
    dispatch,
    brandUUID,
    stockFilter,
    featureFlagFilter,
    refreshFilterPreview,
  ]);

  useEffect((): void => {
    if (!categoryUUID) {
      return;
    }

    const category: ICategory | undefined = Category.categories.find(
      (c: ICategory): boolean => c.uuid === categoryUUID,
    );

    if (
      category?.property.codes.length &&
      Products.categoryProperties.length === 0
    ) {
      dispatch(
        loadCategoryProperties({
          category: {
            uuid: categoryUUID,
            propertyCodes: category.property.codes,
          },
        }),
      );
    }
  }, [
    categoryUUID,
    Category.categories,
    Products.categoryProperties.length,
    dispatch,
  ]);

  // Stock/feature-flag chip options narrow by brand and confirmed property
  // filters, same as availableValues — re-fetched whenever any of those
  // change, not just once per category.
  useEffect((): void => {
    if (!categoryUUID) {
      return;
    }

    const brand: { uuid: string } | undefined =
      brandUUID === BRAND_FILTER_DEFAULT ? undefined : { uuid: brandUUID };

    dispatch(
      loadCategoryAvailability({
        category: { uuid: categoryUUID },
        filters: propertyFilters,
        brand,
      }),
    );
  }, [categoryUUID, brandUUID, propertyFilters, dispatch]);

  useEffect((): void => {
    if (!categoryUUID) {
      return;
    }

    const isCategoryChange: boolean = categoryUUID !== Products.category.uuid;
    const isInitialLoad: boolean = Products.category.uuid === '';

    if (
      isCategoryChange &&
      !isInitialLoad &&
      categoryLoadHandledRef.current !== categoryUUID
    ) {
      categoryLoadHandledRef.current = categoryUUID;
      hasAttemptedLoad.current = true;
      dispatch(
        load({
          category: { uuid: categoryUUID },
          page: 1,
        }),
      );

      return;
    }

    const stock: EStockFilter | undefined =
      stockFilter === STOCK_FILTER_DEFAULT ? undefined : stockFilter;
    const featureFlags: EFeatureFlag[] | undefined =
      featureFlagFilter.length > 0 ? featureFlagFilter : undefined;

    if (isCategoryChange || pageInt !== Products.page) {
      if (propertyFiltersRef.current.length > 0) {
        hasAttemptedLoad.current = true;
        dispatch(
          loadByProperties({
            category: { uuid: categoryUUID },
            filters: propertyFiltersRef.current,
            page: pageInt,
            brand:
              brandUUID === BRAND_FILTER_DEFAULT
                ? undefined
                : { uuid: brandUUID },
            stock,
            featureFlags,
          }),
        );

        return;
      }

      if (brandUUID === BRAND_FILTER_DEFAULT) {
        hasAttemptedLoad.current = true;
        dispatch(
          load({
            category: { uuid: categoryUUID },
            page: pageInt,
            stock,
            featureFlags,
          }),
        );
      } else {
        if (pageInt !== Products.page && Products.pages < pageInt) {
          setPageInt(1);
        }

        hasAttemptedLoad.current = true;
        dispatch(
          loadByBrand({
            category: { uuid: categoryUUID },
            brand: { uuid: brandUUID },
            page: pageInt,
            stock,
            featureFlags,
          }),
        );
        dispatch(
          eventSend({
            type: EEventType.PRODUCTS_FILTER_BY_BRAND,
            action: EEventContextAction.FILTER,
            context: { brand: { uuid: brandUUID } },
          }),
        );
      }
    }

    if (
      categoryUUID === Products.category.uuid &&
      Products.products.length === 0
    ) {
      hasAttemptedLoad.current = true;
      dispatch(
        load({
          category: { uuid: categoryUUID },
          page: pageInt,
        }),
      );
    }
  }, [
    brandUUID,
    stockFilter,
    featureFlagFilter,
    categoryUUID,
    pageInt,
    propertyFilters,
    Products.category.uuid,
    Products.page,
    dispatch,
  ]);

  const handleDraftChange = useCallback(
    (draft: IPropertyFilter[]): void => {
      const brand: { uuid: string } | undefined =
        brandUUID === BRAND_FILTER_DEFAULT ? undefined : { uuid: brandUUID };

      refreshFilterPreview(draft, brand, stockFilter);
    },
    [brandUUID, refreshFilterPreview, stockFilter],
  );

  const handlePropertyFiltersChange = useCallback(
    (nextFilters: IPropertyFilter[]): void => {
      setPropertyFilters(nextFilters);
      setPageInt(1);
      propertyFiltersRef.current = nextFilters;
      setSearchParams(
        (prev: URLSearchParams): URLSearchParams =>
          applyFiltersToSearchParams(prev, nextFilters),
        { replace: true },
      );

      if (!categoryUUID) {
        return;
      }

      const brand: { uuid: string } | undefined =
        brandUUID === BRAND_FILTER_DEFAULT ? undefined : { uuid: brandUUID };
      const stock: EStockFilter | undefined =
        stockFilter === STOCK_FILTER_DEFAULT ? undefined : stockFilter;
      const featureFlags: EFeatureFlag[] | undefined =
        featureFlagFilter.length > 0 ? featureFlagFilter : undefined;

      refreshFilterPreview(nextFilters, brand, stockFilter);
      dispatchProductsLoad({
        filters: nextFilters,
        brand,
        stock,
        featureFlags,
        page: 1,
      });
    },
    [
      brandUUID,
      stockFilter,
      featureFlagFilter,
      categoryUUID,
      dispatchProductsLoad,
      refreshFilterPreview,
      setSearchParams,
    ],
  );

  const handleStockFilterChange = useCallback(
    (nextStockFilter: EStockFilter): void => {
      setStockFilter(nextStockFilter);
      setPageInt(1);
      setSearchParams(
        (prev: URLSearchParams): URLSearchParams =>
          applyStockToSearchParams(prev, nextStockFilter),
        { replace: true },
      );

      if (!categoryUUID) {
        return;
      }

      const brand: { uuid: string } | undefined =
        brandUUID === BRAND_FILTER_DEFAULT ? undefined : { uuid: brandUUID };
      const stock: EStockFilter | undefined =
        nextStockFilter === STOCK_FILTER_DEFAULT ? undefined : nextStockFilter;
      const featureFlags: EFeatureFlag[] | undefined =
        featureFlagFilter.length > 0 ? featureFlagFilter : undefined;

      dispatchProductsLoad({
        filters: propertyFiltersRef.current,
        brand,
        stock,
        featureFlags,
        page: 1,
      });

      refreshFilterPreview(propertyFiltersRef.current, brand, nextStockFilter);
    },
    [
      brandUUID,
      categoryUUID,
      dispatchProductsLoad,
      featureFlagFilter,
      refreshFilterPreview,
      setSearchParams,
    ],
  );

  const handleFeatureFlagFilterChange = useCallback(
    (nextFeatureFlagFilter: EFeatureFlag[]): void => {
      setFeatureFlagFilter(nextFeatureFlagFilter);
      setPageInt(1);
      setSearchParams(
        (prev: URLSearchParams): URLSearchParams =>
          applyFeatureFlagsToSearchParams(prev, nextFeatureFlagFilter),
        { replace: true },
      );

      if (!categoryUUID) {
        return;
      }

      const brand: { uuid: string } | undefined =
        brandUUID === BRAND_FILTER_DEFAULT ? undefined : { uuid: brandUUID };
      const stock: EStockFilter | undefined =
        stockFilter === STOCK_FILTER_DEFAULT ? undefined : stockFilter;
      const featureFlags: EFeatureFlag[] | undefined =
        nextFeatureFlagFilter.length > 0 ? nextFeatureFlagFilter : undefined;

      // No refreshFilterPreview call here: filteredTotal/availableValues/
      // availableStockOptions/availableFeatureFlags are all computed from
      // property filters, brand and stock only — feature flags don't
      // narrow any of them, so there's nothing to refresh.
      dispatchProductsLoad({
        filters: propertyFiltersRef.current,
        brand,
        stock,
        featureFlags,
        page: 1,
      });
    },
    [
      brandUUID,
      categoryUUID,
      dispatchProductsLoad,
      stockFilter,
      setSearchParams,
    ],
  );

  if (Products.isLoading) {
    return <ProductsSkeleton />;
  }

  const category: ICategory | undefined = Category.categories.find(
    (c: ICategory): boolean => c.uuid === categoryUUID,
  );

  const categoryName: string | null = getTranslation(
    category?.names,
    Language.languageType,
  );

  if (typeof document !== 'undefined' && categoryName) {
    const { title } = getHead(t, categoryName, pageInt);
    document.title = title;
  }

  const header: string =
    Products.pages > 1
      ? `${categoryName} [${pageInt} / ${Products.pages}]`
      : `${categoryName || ''}`;

  const brands: IBrand[] | undefined = category?.brands;
  const hasBrandFilter = Boolean(brands && brands.length > 1);
  const hasStockFilter = hasVisibleStockOptions(
    Products.availableStockOptions,
    stockFilter,
  );
  const hasFeatureFlagFilter = hasVisibleFeatureFlagOptions(
    Products.availableFeatureFlags,
    featureFlagFilter,
  );
  const hasSidebar =
    hasBrandFilter ||
    hasStockFilter ||
    hasFeatureFlagFilter ||
    Products.categoryProperties.length > 0;

  const getPageLink = (pageNum: number): string => {
    if (!category) {
      return '';
    }
    const base = getCategoryUrl(category, Language.languageType.code);
    const qs = searchParams.toString();
    return base + (pageNum > 1 ? `/${pageNum}` : '') + (qs ? `?${qs}` : '');
  };

  return (
    <>
      <div className="container">
        <Breadcrumbs />
        <h1>{header}</h1>
      </div>

      <div
        className={`products-layout${hasSidebar ? '' : ' products-layout--full'}`}
      >
        {hasSidebar && (
          <aside className="products-layout__sidebar">
            <PropertyFilters
              properties={Products.categoryProperties}
              filters={propertyFilters}
              filteredTotal={Products.filteredTotal}
              availableValues={Products.availableValues}
              languageType={Language.languageType}
              onChange={handlePropertyFiltersChange}
              stockFilter={
                hasStockFilter ? (
                  <StockQuickFilter
                    value={stockFilter}
                    availableOptions={Products.availableStockOptions}
                    onChange={handleStockFilterChange}
                  />
                ) : null
              }
              featureFlagFilter={
                hasFeatureFlagFilter ? (
                  <FeatureFlagQuickFilter
                    value={featureFlagFilter}
                    availableFlags={Products.availableFeatureFlags}
                    onChange={handleFeatureFlagFilterChange}
                  />
                ) : null
              }
              onDraftChange={handleDraftChange}
              brandFilter={
                brands && hasBrandFilter ? (
                  <Formik
                    enableReinitialize
                    initialValues={{
                      brandUUID,
                    }}
                    onSubmit={(): void => {}}
                  >
                    {(): React.JSX.Element => (
                      <Form className="pf-group">
                        <label
                          className="pf-group__label"
                          htmlFor="brandUUID-simple"
                        >
                          <Trans>products.filter.brandName</Trans>
                        </label>
                        <Field
                          as="select"
                          className="pf-select"
                          name="brandUUID"
                          id="brandUUID-simple"
                          onChange={({ target }): void => {
                            const brandUUIDChanged: string = target.value;
                            setBrandUUID(brandUUIDChanged);

                            const brand: { uuid: string } | undefined =
                              brandUUIDChanged === BRAND_FILTER_DEFAULT
                                ? undefined
                                : { uuid: brandUUIDChanged };

                            setSearchParams(
                              (prev: URLSearchParams): URLSearchParams =>
                                applyBrandToSearchParams(prev, brand?.uuid),
                              { replace: true },
                            );

                            if (!categoryUUID) {
                              return;
                            }

                            const stock: EStockFilter | undefined =
                              stockFilter === STOCK_FILTER_DEFAULT
                                ? undefined
                                : stockFilter;
                            const featureFlags: EFeatureFlag[] | undefined =
                              featureFlagFilter.length > 0
                                ? featureFlagFilter
                                : undefined;

                            dispatchProductsLoad({
                              filters: propertyFiltersRef.current,
                              brand,
                              stock,
                              featureFlags,
                              page: pageInt,
                            });

                            if (brand) {
                              dispatch(
                                eventSend({
                                  type: EEventType.PRODUCTS_FILTER_BY_BRAND,
                                  action: EEventContextAction.FILTER,
                                  context: { brand },
                                }),
                              );
                            }

                            refreshFilterPreview(
                              propertyFiltersRef.current,
                              brand,
                              stockFilter,
                            );
                          }}
                        >
                          <option
                            value={BRAND_FILTER_DEFAULT}
                            key={BRAND_FILTER_DEFAULT}
                          >
                            {t('products.filter.allBrands', {
                              defaultValue: 'All brands',
                            })}
                          </option>
                          {brands.map((c: IBrand): React.JSX.Element => (
                            <option value={c.uuid} key={c.uuid}>
                              {c.name}
                            </option>
                          ))}
                        </Field>
                      </Form>
                    )}
                  </Formik>
                ) : null
              }
            />
          </aside>
        )}

        <div className="products-layout__main">
          {!Products.isLoading &&
          Products.products.length === 0 &&
          hasAttemptedLoad.current ? (
            <div className="container">
              <p>{t('products.noProducts')}</p>
            </div>
          ) : (
            <Grid products={Products.products} headerLevel={2} />
          )}

          {category && (
            <Pagination
              page={Products.page}
              pages={Products.pages}
              getPageLink={getPageLink}
            />
          )}
        </div>
      </div>

      {category && <LanguageMutationLinks category={category} />}
    </>
  );
}

const mapStateToProps = ({ Category, Language, Products }) => ({
  Category,
  Language,
  Products,
});

export default connect(mapStateToProps)(ProductsContainer);
